import { type RouteRecordRaw } from 'vue-router'
import { checkAccessRightsGuard, invalidateServerDataGuard, refreshSessionGuard } from '@/admin/router/guards'
import { ADMIN_ROUTE_NAMES, ADMIN_ROUTE_PATHS } from '@/admin/types/router'
import { FieldContextEnum } from '@/common/types/fields'
import { ScreenTypeEnum } from '@/common/types/screen'
import { ResourceEnum } from '@/common/types/general'
import { FileDefinitionTypeEnum } from '@/common/types/fileDefinition'
import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { queryKeyFactory } from '@/common/services/serverData/serverDataManager'
import { SERVER_DATA_SCOPE, SERVER_DATA_TYPE } from '@/common/types/cacheConstants'
const OutboundMessagesLandingPage = () => import('@/admin/components/sms/outbound-messages/OutboundMessagesLandingPage.vue')

const VoicebotConfiguration = () => import('@/admin/components/voice/voicebot/VoicebotConfiguration.vue')
const VoicebotInboundCallConfigurationPage = () => import('@/admin/components/voice/voicebot/inbound/VoicebotInboundCallConfigurationPage.vue')

const RedirectBackHandlingPage = () => import('@/admin/views/RedirectBackHandlingPage.vue')

const AdminView = () => import('@/admin/views/AdminView.vue')
const LegacyFrame = () => import('@/common/components/legacy/LegacyFrame.vue')

const EditUsersSkillsPage = () => import('@/admin/components/users/EditUserSkills.vue')
const ReasonsLandingPage = () => import('@/admin/components/reasons/ReasonsLandingPage.vue')
const ReasonsCreationPage = () => import('@/admin/components/reasons/ReasonsCreationPage.vue')
const ReasonsEditionPage = () => import('@/admin/components/reasons/ReasonsEditionPage.vue')
const AxialysConfiguration = () => import('@/admin/components/voice/axialys/AxialysConfiguration.vue')
const AxialysOutBoundCallConfigurationPage = () => import('@/admin/components/voice/axialys/outbound/AxialysOutboundCallConfigurationPage.vue')
const AxialysInboundCallConfigurationPage = () => import('@/admin/components/voice/axialys/inbound/AxialysInboundCallConfigurationPage.vue')
const GenesysConfiguration = () => import('@/admin/components/voice/genesys/GenesysConfiguration.vue')
const GenesysInboundCallConfigurationPage = () => import('@/admin/components/voice/genesys/inbound/GenesysInboundCallConfigurationPage.vue')
const GenesysOutboundCallConfigurationPage = () => import('@/admin/components/voice/genesys/outbound/GenesysOutboundCallConfigurationPage.vue')
const UnifiedCtiConfiguration = () => import('@/admin/components/voice/unified-cti/UnifiedCtiConfiguration.vue')
const UnifiedCtiInboundCallConfigurationPage = () => import('@/admin/components/voice/unified-cti/inbound/UnifiedCtiInboundCallConfigurationPage.vue')
const UnifiedCtiOutboundCallConfigurationPage = () =>
  import('@/admin/components/voice/unified-cti/outbound/UnifiedCtiOutboundCallConfigurationPage.vue')
const Five9Configuration = () => import('@/admin/components/voice/five9/Five9Configuration.vue')
const Five9InboundCallConfigurationPage = () => import('@/admin/components/voice/five9/inbound/Five9InboundCallConfigurationPage.vue')
const Five9OutboundCallConfigurationPage = () => import('@/admin/components/voice/five9/outbound/Five9OutboundCallConfigurationPage.vue')
const VersionPage = () => import('@/admin/components/settings/VersionPage.vue')
const SearchConfigLandingPage = () => import('@/admin/components/search/SearchConfigLandingPage.vue')
const SearchConfigCreationPage = () => import('@/admin/components/search/SearchConfigCreationPage.vue')
const SearchConfigEditionPage = () => import('@/admin/components/search/SearchConfigEditionPage.vue')
const ActivitiesListPage = () => import('@/admin/components/activities/ActivitiesListPage.vue')
const ActivitiesEditionPage = () => import('@/admin/components/activities/ActivitiesEditionPage.vue')
const ActivitiesCreationPage = () => import('@/admin/components/activities/ActivitiesCreationPage.vue')
const AIConfigurationPage = () => import('@/admin/components/ai/AiConfigurationPage.vue')
const CustomerSettingsPage = () => import('@/admin/components/settings/CustomerSettingsPage.vue')
const JobsLogsReport = () => import('@/admin/components/automation/JobsLogsReport.vue')
const TicketArchivingPage = () => import('@/admin/components/automation/TicketArchiving.vue')
const AuditLandigPage = () => import('@/admin/components/audit/AuditLandingPage.vue')
const AISummarizeLandingPage = () => import('@/admin/components/ai/AISummarizeLandingPage.vue')
const AIAutomaticResolutionNotes = () => import('@/admin/components/ai/AiAutomaticResolutionNotes.vue')
const ExternalReferentialCustomerSettings = () => import('@/admin/components/settings/ExternalReferentialCustomerSettings.vue')
const MailStorageSettings = () => import('@/admin/components/settings/MailStorageSettings.vue')
const InTransitMailJobConfiguration = () => import('@/admin/components/settings/InTransitMailJobConfiguration.vue')

const VonageConfigurationLandingPage = () => import('@/admin/components/sms/vonage/configurations/VonageConfigurationLandingPage.vue')
const VonageConfigurationCreationPage = () => import('@/admin/components/sms/vonage/configurations/VonageConfigurationCreationPage.vue')
const VonageConfigurationEditPage = () => import('@/admin/components/sms/vonage/configurations/VonageConfigurationEditionPage.vue')
const VonageSmsSenderEditPage = () => import('@/admin/components/sms/vonage/smsNumbers/VonageSmsNumberEditionPage.vue')
const VonageSmsSenderCreationPage = () => import('@/admin/components/sms/vonage/smsNumbers/VonageSmsNumberCreationPage.vue')
const VonageWhatsAppAccountPage = () => import('@/admin/components/social/whatsapp/vonage/VonageWhatsAppAccountPage.vue')
// listing
const ListLandingPageContent = () => import('@/admin/components/listing/ListLandingPageContent.vue')
const ListEditPageContent = () => import('@/admin/components/listing/ListEditPageContent.vue')

const customTypesListPage = () => import('@/admin/components/interactions/customTypes/CustomTypesListPage.vue')
const customTypesEditionPage = () => import('@/admin/components/interactions/customTypes/CustomTypesEditionPage.vue')
const customTypesCreationPage = () => import('@/admin/components/interactions/customTypes/CustomTypesCreationPage.vue')

const CompanyRecordLandingPage = () => import('@/admin/components/company/CompanyRecordLandingPage.vue')

const ContactsLandingPage = () => import('@/admin/components/contact-lists/ContactListsLandingPage.vue')
const ContactListsCreationPage = () => import('@/admin/components/contact-lists/ContactListsCreationPage.vue')
const ContactListsEditionPage = () => import('@/admin/components/contact-lists/ContactListsEditionPage.vue')

const CompanySettingsPage = () => import('@/admin/components/entities/CompanySettingsPage.vue')
// export
const ExportLandingPageContent = () => import('@/admin/components/export/ExportLandingPageContent.vue')
const ExportEditPageContent = () => import('@/admin/components/export/ExportEditPageContent.vue')

// settings
const WorkflowPage = () => import('@/admin/components/workflow/WorkflowPage.vue')
const RegionalSettingsPage = () => import('@/admin/components/settings/RegionalSettingsPage.vue')

const routes: RouteRecordRaw[] = [
  {
    name: ADMIN_ROUTE_NAMES.ADMIN_ROOT,
    path: '/assist/admin',
    component: AdminView,
    beforeEnter: [checkAccessRightsGuard, invalidateServerDataGuard],
    meta: { admin: true },
    children: [
      {
        path: 'rules',
        redirect: '/assist/admin/rules/list',
      },
      {
        path: 'products/:id',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.PRODUCTS,
            }),
          ],
        },
      },
      {
        path: 'products',
        redirect: '/assist/admin/products/0',
      },
      {
        path: 'activities',
        name: 'admin-activities-list',
        component: ActivitiesListPage,
      },
      {
        path: 'users/edit/:login/skills',
        name: 'admin-edit-user-skills',
        component: EditUsersSkillsPage,
        props: true,
        beforeEnter: refreshSessionGuard,
      },
      {
        path: 'activities/edit/:activityId',
        component: ActivitiesEditionPage,
        name: 'admin-activities-edit',
        props: (route) => ({ activityId: route.params.activityId }),
      },
      {
        path: 'activities/create',
        name: 'admin-activities-create',
        component: ActivitiesCreationPage,
        props: true,
      },
      {
        path: 'entityRecord',
        name: 'admin-company-record-landing-page',
        component: CompanyRecordLandingPage,
      },
      {
        path: 'contacts',
        name: 'admin-contact-lists-landing-page',
        component: ContactsLandingPage,
      },
      {
        path: 'contacts/create',
        name: 'admin-contact-lists-create',
        component: ContactListsCreationPage,
        props: true,
      },
      {
        path: 'contacts/edit/:contactListId',
        component: ContactListsEditionPage,
        name: 'admin-contact-lists-edit',
        props: (route) => ({ contactListId: route.params.contactListId }),
      },
      {
        path: 'reasons',
        name: 'admin-reasons-landing-page',
        component: ReasonsLandingPage,
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.REASONS,
            }),
          ],
        },
      },
      {
        path: 'reasons/create/:actionType',
        name: 'admin-reason-create',
        component: ReasonsCreationPage,
        props: true,
      },
      {
        path: 'reasons/edit/:code',
        name: 'admin-reason-edit',
        component: ReasonsEditionPage,
        props: true,
      },
      {
        path: 'voice/axialys-configuration/',
        name: 'admin-axialys-configuration',
        component: AxialysConfiguration,
      },
      {
        path: 'voice/axialys-configuration/outbound/edit/:id',
        name: 'admin-axialys-outbound-configuration-edit',
        component: AxialysOutBoundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/axialys-configuration/outbound/create',
        name: 'admin-axialys-outbound-configuration-create',
        component: AxialysOutBoundCallConfigurationPage,
      },
      {
        path: 'voice/axialys-configuration/inbound/edit/:id',
        name: 'admin-axialys-inbound-configuration-edit',
        component: AxialysInboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/axialys-configuration/inbound/create',
        name: 'admin-axialys-inbound-configuration-create',
        component: AxialysInboundCallConfigurationPage,
      },
      {
        path: 'voice/voicebot-configuration/',
        name: 'admin-voicebot-configuration',
        component: VoicebotConfiguration,
      },
      {
        path: 'audit/',
        name: 'audit',
        component: AuditLandigPage,
      },
      {
        path: 'voice/voicebot-configuration/inbound/edit/:id',
        name: 'admin-voicebot-inbound-configuration-edit',
        component: VoicebotInboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/voicebot-configuration/inbound/create',
        name: 'admin-voicebot-inbound-configuration-create',
        component: VoicebotInboundCallConfigurationPage,
      },
      {
        path: 'voice/genesys-configuration/',
        name: 'admin-genesys-configuration',
        component: GenesysConfiguration,
      },
      {
        path: 'voice/genesys-configuration/inbound/edit/:id',
        name: 'admin-genesys-inbound-configuration-edit',
        component: GenesysInboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/genesys-configuration/inbound/create',
        name: 'admin-genesys-inbound-configuration-create',
        component: GenesysInboundCallConfigurationPage,
      },
      {
        path: 'voice/genesys-configuration/outbound/edit/:id',
        name: 'admin-genesys-outbound-configuration-edit',
        component: GenesysOutboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/genesys-configuration/outbound/create',
        name: 'admin-genesys-outbound-configuration-create',
        component: GenesysOutboundCallConfigurationPage,
      },
      {
        path: 'voice/unified-cti-configuration/',
        name: 'admin-unified-cti-configuration',
        component: UnifiedCtiConfiguration,
      },
      {
        path: 'voice/unified-cti-configuration/inbound/edit/:id',
        name: 'admin-unified-cti-inbound-configuration-edit',
        component: UnifiedCtiInboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/unified-cti-configuration/inbound/create',
        name: 'admin-unified-cti-inbound-configuration-create',
        component: UnifiedCtiInboundCallConfigurationPage,
      },
      {
        path: 'voice/unified-cti-configuration/outbound/edit/:id',
        name: 'admin-unified-cti-outbound-configuration-edit',
        component: UnifiedCtiOutboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/unified-cti-configuration/outbound/create',
        name: 'admin-unified-cti-outbound-configuration-create',
        component: UnifiedCtiOutboundCallConfigurationPage,
      },
      {
        path: 'voice/five9-configuration/',
        name: 'admin-five9-configuration',
        component: Five9Configuration,
      },
      {
        path: 'voice/five9-configuration/inbound/create',
        name: 'admin-five9-inbound-configuration-create',
        component: Five9InboundCallConfigurationPage,
      },
      {
        path: 'voice/five9-configuration/inbound/edit/:id',
        name: 'admin-five9-inbound-configuration-edit',
        component: Five9InboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/five9-configuration/outbound/edit/:id',
        name: 'admin-five9-outbound-configuration-edit',
        component: Five9OutboundCallConfigurationPage,
        props: true,
      },
      {
        path: 'voice/five9-configuration/outbound/create',
        name: 'admin-five9-outbound-configuration-create',
        component: Five9OutboundCallConfigurationPage,
      },
      {
        path: 'outbound-messages/list',
        name: 'admin-outbound-messages-landing',
        component: OutboundMessagesLandingPage,
      },
      {
        path: 'vonage/configurations',
        name: 'admin-vonage-configuration-landing',
        component: VonageConfigurationLandingPage,
      },
      {
        path: 'vonage/configurations/create',
        name: 'admin-vonage-configuration-create',
        component: VonageConfigurationCreationPage,
      },
      {
        path: 'vonage/configurations/edit/:id',
        name: 'admin-vonage-configuration-edit',
        component: VonageConfigurationEditPage,
        props: true,
      },
      {
        path: 'sms/senders/vonage/edit/:id',
        name: 'admin-vonage-sms-sender-edit',
        component: VonageSmsSenderEditPage,
        props: true,
      },
      {
        path: 'sms/senders/vonage/create',
        name: 'admin-vonage-sms-sender-create',
        component: VonageSmsSenderCreationPage,
      },
      {
        path: 'social/whatsApp/vonage/edit/:id',
        name: 'admin-vonage-whatsapp-account-edit',
        component: VonageWhatsAppAccountPage,
        props: true,
      },
      {
        path: 'social/whatsApp/vonage/create',
        name: 'admin-vonage-whatsapp-account-create',
        component: VonageWhatsAppAccountPage,
      },
      {
        path: 'versions/',
        component: VersionPage,
      },
      {
        path: 'regional',
        component: RegionalSettingsPage,
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.LOCALES,
            }),
          ],
        },
      },
      {
        path: 'entities/settings',
        name: ADMIN_ROUTE_NAMES.ADMIN_ENTITIES_SETTINGS,
        component: CompanySettingsPage,
        meta: {
          breadcrumb: {
            label: 'assist.admin.company.entity.settings.title',
          },
        },
      },
      {
        path: 'search',
        name: 'admin-search-landing-page',
        component: SearchConfigLandingPage,
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.I18n,
              type: SERVER_DATA_TYPE.ITEM,
              params: PrefixTranslationsEnum.SEARCH_CONFIG,
            }),
          ],
        },
      },
      {
        path: 'search/create',
        name: 'admin-search-create',
        component: SearchConfigCreationPage,
      },
      {
        path: 'search/edit/:configId',
        name: 'admin-search-edit',
        component: SearchConfigEditionPage,
        props: true,
      },
      {
        path: 'settings/customer/view',
        name: 'admin-customer-settings',
        component: CustomerSettingsPage,
      },
      {
        path: 'ai',
        name: 'ai-configuration',
        component: AIConfigurationPage,
      },
      {
        path: 'tickets/export/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_CREATE_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.TICKET_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_TICKET,
          resource: ResourceEnum.TICKET,
        }),
      },
      {
        path: 'tickets/export/edit/:exportDefCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_EDIT_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: (route) => ({
          exportDefCode: route.params?.exportDefCode,
          fieldContext: FieldContextEnum.TICKET_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_TICKET,
          resource: ResourceEnum.TICKET,
        }),
      },
      {
        path: 'tickets/export',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_LANDING_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ExportLandingPageContent,
        props: () => ({
          exportType: FileDefinitionTypeEnum.EXPORT_TICKET,
          resource: ResourceEnum.TICKET,
        }),
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FILE_DEFINITION,
            }),
          ],
        },
      },
      {
        path: 'customers/export/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_CREATE_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.CUSTOMER_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_CUSTOMER,
          resource: ResourceEnum.CUSTOMER,
        }),
      },
      {
        path: 'customers/export/edit/:exportDefCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_EDIT_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: (route) => ({
          exportDefCode: route.params?.exportDefCode,
          fieldContext: FieldContextEnum.CUSTOMER_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_CUSTOMER,
          resource: ResourceEnum.CUSTOMER,
        }),
      },
      {
        path: 'customers/export',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_LANDING_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ExportLandingPageContent,
        props: () => ({
          exportType: FileDefinitionTypeEnum.EXPORT_CUSTOMER,
          resource: ResourceEnum.CUSTOMER,
        }),
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FILE_DEFINITION,
            }),
          ],
        },
      },
      {
        path: 'interactions/export/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_CREATE_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.INTERACTION_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_INTERACTION,
          resource: ResourceEnum.INTERACTION,
        }),
      },
      {
        path: 'interactions/export/edit/:exportDefCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_EDIT_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ExportEditPageContent,
        meta: {
          exportEdit: true,
        },
        props: (route) => ({
          exportDefCode: route.params?.exportDefCode,
          fieldContext: FieldContextEnum.INTERACTION_EXPORT,
          exportType: FileDefinitionTypeEnum.EXPORT_INTERACTION,
          resource: ResourceEnum.INTERACTION,
        }),
      },
      {
        path: 'interactions/export',
        name: ADMIN_ROUTE_NAMES.ADMIN_EXPORT_LANDING_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ExportLandingPageContent,
        props: () => ({
          exportType: FileDefinitionTypeEnum.EXPORT_INTERACTION,
          resource: ResourceEnum.INTERACTION,
        }),
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FILE_DEFINITION,
            }),
          ],
        },
      },
      {
        path: 'tickets/list',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_LANDING_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ListLandingPageContent,
        meta: {
          listingLanding: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V1,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
          ],
        },
        props: () => ({
          screenType: ScreenTypeEnum.TICKETLIST,
          resource: ResourceEnum.TICKET,
        }),
      },
      {
        path: 'tickets/list/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_CREATE_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.TICKET_LIST,
          screenType: ScreenTypeEnum.TICKETLIST,
          resource: ResourceEnum.TICKET,
        }),
      },
      {
        path: 'tickets/list/edit/:screenCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_EDIT_ROUTE_PREFIX + ResourceEnum.TICKET,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: (route) => ({
          screenCode: route.params?.screenCode,
          fieldContext: FieldContextEnum.TICKET_LIST,
          screenType: ScreenTypeEnum.TICKETLIST,
          resource: ResourceEnum.TICKET,
        }),
      },
      {
        path: 'entityRecord',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_LANDING_ROUTE_PREFIX + ResourceEnum.COMPANY,
        component: CompanyRecordLandingPage,
      },
      {
        path: 'customers/list',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_LANDING_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ListLandingPageContent,
        meta: {
          listingLanding: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V1,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
          ],
        },
        props: () => ({
          screenType: ScreenTypeEnum.CLIENTLIST,
          resource: ResourceEnum.CUSTOMER,
        }),
      },
      {
        path: 'entities/list',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_LANDING_ROUTE_PREFIX + ResourceEnum.COMPANY,
        component: ListLandingPageContent,
        meta: {
          listingLanding: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V1,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
          ],
        },
        props: () => ({
          screenType: ScreenTypeEnum.COMPANY,
          resource: ResourceEnum.COMPANY,
        }),
      },
      {
        path: 'entities/list/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_CREATE_ROUTE_PREFIX + ResourceEnum.COMPANY,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.COMPANY_LIST,
          screenType: ScreenTypeEnum.COMPANY,
          resource: ResourceEnum.COMPANY,
        }),
      },
      {
        path: 'entities/list/edit/:screenCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_EDIT_ROUTE_PREFIX + ResourceEnum.COMPANY,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: (route) => ({
          screenCode: route.params?.screenCode,
          fieldContext: FieldContextEnum.COMPANY_LIST,
          screenType: ScreenTypeEnum.COMPANY,
          resource: ResourceEnum.COMPANY,
        }),
      },
      {
        path: 'ai/summarize',
        name: 'admin-ai-summarize-landing-page',
        component: AISummarizeLandingPage,
      },
      {
        path: 'ai/automatic-resolution-notes',
        name: 'admin-ai-automatic-resolution-notes-landing-page',
        component: AIAutomaticResolutionNotes,
      },
      {
        path: ADMIN_ROUTE_PATHS.EXTERNAL_REFERENTIAL_CUSTOMER_MS,
        name: 'admin-external-referential-customer-settings',
        component: ExternalReferentialCustomerSettings,
      },
      {
        path: ADMIN_ROUTE_PATHS.MAIL_STORAGE_SETTINGS,
        name: 'admin-mail-storage-settings',
        component: MailStorageSettings,
      },
      {
        path: ADMIN_ROUTE_PATHS.IN_TRANSIT_MAIL_JOB_CONFIGURATION,
        name: 'admin-in-transit-mail-job-configuration',
        component: InTransitMailJobConfiguration,
      },
      {
        path: 'customers/list/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_CREATE_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.CUSTOMER_LIST,
          screenType: ScreenTypeEnum.CLIENTLIST,
          resource: ResourceEnum.CUSTOMER,
        }),
      },
      {
        path: 'customers/list/edit/:screenCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_EDIT_ROUTE_PREFIX + ResourceEnum.CUSTOMER,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: (route) => ({
          screenCode: route.params?.screenCode,
          fieldContext: FieldContextEnum.CUSTOMER_LIST,
          screenType: ScreenTypeEnum.CLIENTLIST,
          resource: ResourceEnum.CUSTOMER,
        }),
      },
      {
        path: 'interactions/list',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_LANDING_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ListLandingPageContent,
        meta: {
          listingLanding: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V1,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
          ],
        },
        props: () => ({
          screenType: ScreenTypeEnum.INTERACTIONLIST,
          resource: ResourceEnum.INTERACTION,
        }),
      },
      {
        path: 'interactions/list/create',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_CREATE_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: () => ({
          fieldContext: FieldContextEnum.INTERACTION_LIST,
          screenType: ScreenTypeEnum.INTERACTIONLIST,
          resource: ResourceEnum.INTERACTION,
        }),
      },
      {
        path: 'interactions/list/edit/:screenCode',
        name: ADMIN_ROUTE_NAMES.ADMIN_LIST_EDIT_ROUTE_PREFIX + ResourceEnum.INTERACTION,
        component: ListEditPageContent,
        meta: {
          listingEdit: true,
        },
        props: (route) => ({
          screenCode: route.params?.screenCode,
          fieldContext: FieldContextEnum.INTERACTION_LIST,
          screenType: ScreenTypeEnum.INTERACTIONLIST,
          resource: ResourceEnum.INTERACTION,
        }),
      },
      {
        path: 'interactions/custom-types',
        name: 'admin-custom-types-list',
        component: customTypesListPage,
        meta: {
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.I18n,
              type: SERVER_DATA_TYPE.ITEM,
              params: PrefixTranslationsEnum.INTERACTION,
            }),
          ],
        },
      },
      {
        path: 'interactions/custom-types/edit/:id',
        name: 'admin-custom-types-edit',
        component: customTypesEditionPage,
        props: true,
      },
      {
        path: 'interactions/custom-types/create',
        name: 'admin-custom-types-create',
        component: customTypesCreationPage,
      },
      {
        path: 'joblogs',
        name: 'admin-automation-jobslogs',
        component: JobsLogsReport,
      },
      {
        path: 'ticketArchiving',
        name: 'admin-automation-ticket-archiving',
        component: TicketArchivingPage,
      },
      {
        path: 'wkfwTransition/',
        name: 'admin-workflow',
        component: WorkflowPage,
      },
      {
        path: 'sms/senders/add/:id',
        name: 'admin-sms-sender-add',
        component: LegacyFrame,
        meta: {
          legacy: true,
          exclude: ['.*\\/save$'],
        },
      },
      {
        path: 'social/whatsApp/add/:id',
        name: 'admin-whatsapp-account-add',
        component: LegacyFrame,
        meta: {
          legacy: true,
          exclude: ['.*\\/save$'],
        },
      },
      {
        path: 'redirect/back/:foundeverFrom',
        name: 'redirect-back',
        component: RedirectBackHandlingPage,
        props: true,
        meta: {
          technical: true,
        },
      },
      {
        path: 'priority',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.PRIORITIES,
            }),
          ],
        },
      },
      {
        path: 'channels',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.CHANNELS,
            }),
          ],
        },
      },
      {
        path: 'emergency',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.EMERGENCIES,
            }),
          ],
        },
      },
      {
        path: 'trans',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.I18n,
            }),
          ],
        },
      },
      {
        path: 'screens',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V1,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
          ],
        },
      },
      {
        path: 'screensCustomer',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SCREENS_V3,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FIELDS,
            }),
          ],
        },
      },
      {
        path: 'skills',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SKILLS,
            }),
          ],
        },
      },
      {
        path: 'skillGroups',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.SPACES,
            }),
          ],
        },
      },
      {
        path: 'schedules',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.I18n,
              type: SERVER_DATA_TYPE.ITEM,
              params: PrefixTranslationsEnum.SCHEDULE,
            }),
          ],
        },
      },
      {
        path: 'customfields',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.CUSTOM_FIELDS,
            }),
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FIELDS,
            }),
          ],
        },
      },
      {
        path: 'screensCustomer',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.FIELDS,
            }),
          ],
        },
      },

      {
        path: 'typologies',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.TYPOLOGIES,
            }),
          ],
        },
      },
      {
        path: 'groups',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.TEAMS,
            }),
          ],
        },
      },
      {
        path: 'users',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.USERS,
            }),
          ],
        },
      },
      {
        path: 'mail/mailboxes',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.MAILBOXES,
            }),
          ],
        },
      },
      {
        path: 'users/import/:importType',
        component: LegacyFrame,
        meta: {
          legacy: true,
          exclude: ['.*\\/users/import$'],
        },
      },
      {
        path: 'cannedResponses',
        component: LegacyFrame,
        meta: {
          legacy: true,
          invalidateServerData: [
            queryKeyFactory({
              scope: SERVER_DATA_SCOPE.I18n,
              type: SERVER_DATA_TYPE.ITEM,
              params: PrefixTranslationsEnum.REPLY_TEMP,
            }),
          ],
        },
      },
      {
        path: 'cannedResponses/import/:id',
        component: LegacyFrame,
        meta: {
          legacy: true,
          exclude: ['.*\\/cannedResponses/import$'],
        },
      },
      {
        path: ':url+',
        component: LegacyFrame,
        meta: {
          legacy: true,
          exclude: ['.*\\/save$'],
        },
      },
    ],
  },
]
export default routes
